import React from 'react';
import { Card, Typography, Input, Button } from 'antd';
import api from '../config/api';

const { Title, Text } = Typography;

function dot(status: string) {
  const s = (status || '').toLowerCase();
  const color = s === 'operational' ? '#52c41a' : s === 'degraded' ? '#faad14' : s === 'down' ? '#ff4d4f' : '#d9d9d9';
  return <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: color }} />;
}

const StatusPage: React.FC = () => {
  const [status, setStatus] = React.useState<any>({ api: 'unknown', web: 'unknown', sql: 'unknown', updated_at: undefined });
  const [email, setEmail] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [submitting, setSubmitting] = React.useState(false);

  const fetchStatus = React.useCallback(async () => {
    try {
      setError(null);
      // Use fetch with no-store to avoid any caching/CDN interference
      const res = await fetch(`/api/status?_=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setStatus(data || {});
    } catch (e: any) {
      console.error('Status fetch failed:', e);
      setError(e?.message || 'Failed to load status');
    }
  }, []);

  React.useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 15000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  const allOk = ['api', 'web', 'sql'].every(k => (status[k] || '').toLowerCase() === 'operational');
  const bannerColor = allOk ? '#f6ffed' : '#fff2e8';
  const bannerBorder = allOk ? '#b7eb8f' : '#ffbb96';
  const bannerText = allOk ? 'All Systems Operational' : 'Some Systems Experiencing Issues';

  const onSubscribe = async () => {
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post('/api/status/subscribe', { email });
      setEmail('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '32px 16px' }}>
      {/* Banner */}
      <div style={{
        background: bannerColor,
        border: `1px solid ${bannerBorder}`,
        borderRadius: 12,
        padding: '20px 24px',
        marginBottom: 24
      }}>
        <Title level={3} style={{ margin: 0 }}>{bannerText}</Title>
        <Text type="secondary">Last updated: {status.updated_at ? new Date(status.updated_at).toLocaleString() : '—'}</Text>
        {error && (
          <div style={{ marginTop: 8 }}>
            <Text type="danger">Status fetch error: {error}</Text>
          </div>
        )}
      </div>

      {/* Services list */}
      <Card style={{ borderRadius: 12 }} bodyStyle={{ padding: 0 }}>
        {[{ key: 'api', name: 'API' }, { key: 'web', name: 'IIS Web' }, { key: 'sql', name: 'SQL' }].map((svc, i) => (
          <div key={svc.key} style={{ display: 'flex', alignItems: 'center', padding: '14px 18px', borderTop: i === 0 ? 'none' : '1px solid #f0f0f0' }}>
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 10 }}>
              {dot(status[svc.key])}
              <Text strong>{svc.name}</Text>
            </div>
            <div>
              <Text>{(status[svc.key] || 'unknown').toUpperCase()}</Text>
            </div>
          </div>
        ))}
      </Card>

      {/* Subscribe */}
      <Card style={{ borderRadius: 12, marginTop: 24 }}>
        <Title level={4} style={{ marginTop: 0, marginBottom: 12 }}>Subscribe for updates</Title>
        <div style={{ display: 'flex', gap: 8 }}>
          <Input type="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
          <Button type="primary" loading={submitting} onClick={onSubscribe}>Subscribe</Button>
        </div>
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>We’ll email you when the status changes.</Text>
      </Card>
    </div>
  );
};

export default StatusPage;


